// The Peak Lists section of global search: whether it shows at all, what it
// asks for, where a row links to, and what its second line says. Kept out of
// Search.vue so the rules are unit-tested rather than living inline in the
// template.

import { peakBaggingWebVisible } from '../peak_bagging/webVisibility'

/** Rows the section shows. A doorway to the catalogue, not a second one. */
export const PEAK_LIST_RESULT_LIMIT = 3

export interface GeoPoint {
  lat: number
  lng: number
}

export interface SearchPeakListVariables {
  query?: string
  lat?: number
  lon?: number
  page: number
  perPage: number
}

/**
 * The question the section asks, typed or not.
 *
 * With nothing typed it asks for the lists near the walker — the server ranks
 * nearest first whenever a point is given — so search opened in Dublin leads
 * with the Dublin and Wicklow rounds. Once something is typed the point is
 * dropped: with a point the server only returns lists that have a peak inside
 * the radius, and "munros" typed in Dublin has to find the Munros.
 *
 * Without a usable point the empty box falls back to the curated catalogue
 * order. The backend rejects half a coordinate, so a point is sent whole or
 * not at all. Same rule as the apps' searchPeakListRequest.
 */
export function searchPeakListVariables (query: string, geo: GeoPoint | null | undefined): SearchPeakListVariables {
  const trimmed = (query || '').trim()
  const variables: SearchPeakListVariables = { page: 1, perPage: PEAK_LIST_RESULT_LIMIT }

  if (trimmed) {
    variables.query = trimmed
  } else if (geo && Number.isFinite(geo.lat) && Number.isFinite(geo.lng)) {
    variables.lat = geo.lat
    variables.lon = geo.lng
  }

  return variables
}

export interface SearchPeakList {
  id: string
  slug: string
  name: string
  locationLabel?: string | null
  webPath?: string | null
  peaksCount: number
  baggedCount?: number | null
  baggedBadgeUrl?: string | null
  nonBaggedBadgeUrl?: string | null
}

/**
 * Where a row links to: the list's canonical page, or the legacy address for a
 * list with no country yet — the same fallback the apps share a list at.
 */
export function peakListHref (list: Pick<SearchPeakList, 'slug' | 'webPath'>): string {
  return list.webPath || `/peaks/lists/${encodeURIComponent(list.slug)}`
}

/**
 * The row's second line: where the round is, and how far through it the
 * walker is — progress only once something has been bagged, since "0/282" on
 * every untouched row is noise. Matches the apps' peakListSubtitle.
 */
export function peakListSubtitle (list: Pick<SearchPeakList, 'locationLabel' | 'baggedCount' | 'peaksCount'>): string | null {
  const place = (list.locationLabel || '').trim()
  const bagged = list.baggedCount || 0
  if (bagged <= 0) return place || null

  const progress = `${bagged}/${list.peaksCount}`
  return place ? `${place} · ${progress}` : progress
}

export interface SearchPeakListsResult {
  lists: SearchPeakList[]
  total: number
}

const NO_PEAK_LISTS: SearchPeakListsResult = { lists: [], total: 0 }

/** Runs the GraphQL document with the given variables — graph_client in the app, a fake in tests. */
export type SearchPeakListsRequest = (variables: SearchPeakListVariables) => Promise<any>

/**
 * The rows for the section, or none at all.
 *
 * Peak bagging's web surface is not public yet: it shows to admins only until
 * PEAK_BAGGING_WEB is switched on (PeakBagging.visible_to?), and the list pages
 * this section links to 404 for everyone else. The peak GraphQL queries answer
 * regardless — shipped app builds depend on them — so the web has to decide for
 * itself, from the flag Rails renders into the page. Hidden means no request,
 * not just no rows, so the feature does not leak through the network tab.
 *
 * A failure is an empty section rather than an error: one section failing
 * must not take the rest of the search down with it.
 */
export async function loadSearchPeakLists (
  query: string,
  geo: GeoPoint | null | undefined,
  request: SearchPeakListsRequest,
  visible: boolean = peakBaggingWebVisible()
): Promise<SearchPeakListsResult> {
  if (!visible) return NO_PEAK_LISTS

  try {
    const response = await request(searchPeakListVariables(query, geo))
    const lists: SearchPeakList[] = response?.searchPeakLists?.peakLists || []
    return { lists, total: response?.searchPeakLists?.totalCount || lists.length }
  } catch (error) {
    console.error('Error searching peak lists:', error)
    return NO_PEAK_LISTS
  }
}
